import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const IdealWeightCalculatorPage = () => {
  const [unitSystem, setUnitSystem] = useState('metric'); // 'us' or 'metric'
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('25');
  const [heightFeet, setHeightFeet] = useState('5');
  const [heightInches, setHeightInches] = useState('10');
  const [heightCm, setHeightCm] = useState('180');
  const [result, setResult] = useState(null);

  const calculate = () => {
    let totalInches;
    
    if (unitSystem === 'us') {
      const feet = parseFloat(heightFeet) || 0;
      const inches = parseFloat(heightInches) || 0;
      totalInches = feet * 12 + inches;
    } else {
      const cm = parseFloat(heightCm) || 0;
      totalInches = cm / 2.54;
    }

    if (totalInches < 60) {
      // Below 5 feet, formulas don't apply well
      setResult({ error: 'Height must be at least 5 feet (152.4 cm) for accurate calculations.' });
      return;
    }

    const inchesOver5Feet = totalInches - 60;
    let robinson, miller, devine, hamwi;

    if (gender === 'male') {
      robinson = 52 + 1.9 * inchesOver5Feet;
      miller = 56.2 + 1.41 * inchesOver5Feet;
      devine = 50 + 2.3 * inchesOver5Feet;
      hamwi = 48 + 2.7 * inchesOver5Feet;
    } else {
      robinson = 49 + 1.7 * inchesOver5Feet;
      miller = 53.1 + 1.36 * inchesOver5Feet;
      devine = 45.5 + 2.3 * inchesOver5Feet;
      hamwi = 45.5 + 2.2 * inchesOver5Feet;
    }

    // Calculate healthy BMI range (18.5 - 25)
    const heightM = totalInches * 0.0254;
    const bmiLow = 18.5 * heightM * heightM;
    const bmiHigh = 25 * heightM * heightM;

    setResult({
      robinson: robinson.toFixed(1),
      miller: miller.toFixed(1),
      devine: devine.toFixed(1),
      hamwi: hamwi.toFixed(1),
      bmiRangeLow: bmiLow.toFixed(1),
      bmiRangeHigh: bmiHigh.toFixed(1),
      unitSystem
    });
  };

  const clear = () => {
    setAge('25');
    setHeightFeet('5');
    setHeightInches('10');
    setHeightCm('180');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/fitness-health" className="text-cyan-400 hover:text-cyan-300">Fitness & Health</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Ideal Weight Calculator</span>
          </nav>
          
          <h1 className="text-3xl font-bold text-white mb-4">Ideal Weight Calculator</h1>
          <p className="text-gray-400 mb-6">
            The Ideal Weight Calculator computes ideal body weight (IBW) ranges based on height, gender, and age. 
            Currently, there persist several popular formulas, and our calculator provides their results for side-to-side comparisons.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            {/* Unit System Tabs */}
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button 
                onClick={() => setUnitSystem('us')} 
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  unitSystem === 'us' 
                    ? 'border-cyan-500 text-cyan-400' 
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                US Units
              </button>
              <button 
                onClick={() => setUnitSystem('metric')} 
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  unitSystem === 'metric' 
                    ? 'border-cyan-500 text-cyan-400' 
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                Metric Units
              </button>
            </div>

            <div className="space-y-5">
              {/* Age Input */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Age</label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number" 
                    value={age} 
                    onChange={(e) => setAge(e.target.value)} 
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" 
                    min="2"
                    max="80"
                  />
                  <span className="text-gray-400 text-sm">ages 2 - 80</span>
                </div>
              </div>

              {/* Gender Selection */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 font-medium w-20">Gender</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="gender" 
                      checked={gender === 'male'} 
                      onChange={() => setGender('male')}
                      className="w-4 h-4 accent-cyan-500"
                    />
                    <span className="text-gray-300">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="gender" 
                      checked={gender === 'female'} 
                      onChange={() => setGender('female')}
                      className="w-4 h-4 accent-cyan-500"
                    />
                    <span className="text-gray-300">Female</span>
                  </label>
                </div>
              </div>

              {/* Height Input - US Units */}
              {unitSystem === 'us' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 font-medium w-20">Height</label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      value={heightFeet} 
                      onChange={(e) => setHeightFeet(e.target.value)} 
                      className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" 
                    />
                    <span className="text-gray-400">feet</span>
                    <Input 
                      type="number" 
                      value={heightInches} 
                      onChange={(e) => setHeightInches(e.target.value)} 
                      className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" 
                    />
                    <span className="text-gray-400">inches</span>
                  </div>
                </div>
              )}

              {/* Height Input - Metric Units */}
              {unitSystem === 'metric' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 font-medium w-20">Height</label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      value={heightCm} 
                      onChange={(e) => setHeightCm(e.target.value)} 
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white text-center" 
                    />
                    <span className="text-gray-400">cm</span>
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <Button onClick={calculate} className="bg-cyan-600 hover:bg-cyan-500 text-white px-6">
                Calculate
              </Button>
              <Button onClick={clear} variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#2a2a2a]">
                Clear
              </Button>
            </div>

            {/* Results */}
            {result && !result.error && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Result</h3>
                </div>
                <p className="text-gray-400 mb-4">The ideal weight based on popular formulas:</p>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#2a2a2a]">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Formula</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Ideal Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#2a2a2a]">
                        <td className="py-3 px-4 text-gray-300">Robinson (1983)</td>
                        <td className="py-3 px-4 text-right text-white font-bold">{result.robinson} kg</td>
                      </tr>
                      <tr className="border-b border-[#2a2a2a]">
                        <td className="py-3 px-4 text-gray-300">Miller (1983)</td>
                        <td className="py-3 px-4 text-right text-white font-bold">{result.miller} kg</td>
                      </tr>
                      <tr className="border-b border-[#2a2a2a]">
                        <td className="py-3 px-4 text-gray-300">Devine (1974)</td>
                        <td className="py-3 px-4 text-right text-white font-bold">{result.devine} kg</td>
                      </tr>
                      <tr className="border-b border-[#2a2a2a]">
                        <td className="py-3 px-4 text-gray-300">Hamwi (1964)</td>
                        <td className="py-3 px-4 text-right text-white font-bold">{result.hamwi} kg</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-300">Healthy BMI Range</td>
                        <td className="py-3 px-4 text-right text-white font-bold">
                          {result.bmiRangeLow} - {result.bmiRangeHigh} kg
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result && result.error && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400">{result.error}</p>
              </div>
            )}
          </div>

          {/* Related Calculators */}
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/bmi-calculator" className="text-cyan-400 hover:text-cyan-300">BMI Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/body-fat-calculator" className="text-cyan-400 hover:text-cyan-300">Body Fat Calculator</Link>
            <span className="text-gray-600">|</span>
            <Link to="/calorie-calculator" className="text-cyan-400 hover:text-cyan-300">Calorie Calculator</Link>
          </div>

          {/* Information Section */}
          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">How Much Should I Weigh?</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Most everyone has at some point tried to lose weight, or at least known somebody who has. This is largely 
                due to the perception of an "ideal" body weight, which is often based on what we see promoted through various 
                media such as social media, TV, movies, magazines, etc.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Note that IBW is not a perfect measurement. It does not consider the percentages of body fat and muscle in 
                a person's body. This means that it is possible for highly fit, healthy athletes to be considered overweight 
                based on their IBW.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Formulas for Finding the Ideal Weight</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                IBW formulas were developed mainly to facilitate drug dosage calculations. All of the formulas have the 
                same format of a base weight given a height of 5 feet with a set weight increment added per inch over 
                the height of 5 feet.
              </p>

              <div className="space-y-4">
                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">G. J. Hamwi Formula (1964)</h3>
                  <p className="text-gray-400 text-sm">Male: 48.0 kg + 2.7 kg per inch over 5 feet</p>
                  <p className="text-gray-400 text-sm">Female: 45.5 kg + 2.2 kg per inch over 5 feet</p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">B. J. Devine Formula (1974)</h3>
                  <p className="text-gray-400 text-sm">Male: 50.0 kg + 2.3 kg per inch over 5 feet</p>
                  <p className="text-gray-400 text-sm">Female: 45.5 kg + 2.3 kg per inch over 5 feet</p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">J. D. Robinson Formula (1983)</h3>
                  <p className="text-gray-400 text-sm">Male: 52 kg + 1.9 kg per inch over 5 feet</p>
                  <p className="text-gray-400 text-sm">Female: 49 kg + 1.7 kg per inch over 5 feet</p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">D. R. Miller Formula (1983)</h3>
                  <p className="text-gray-400 text-sm">Male: 56.2 kg + 1.41 kg per inch over 5 feet</p>
                  <p className="text-gray-400 text-sm">Female: 53.1 kg + 1.36 kg per inch over 5 feet</p>
                </div>

                <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">Healthy BMI Range</h3>
                  <p className="text-gray-400 text-sm">
                    The World Health Organization's (WHO) recommended healthy BMI range is 18.5 - 25 for both males and females. 
                    Based on the BMI range, it is possible to find out a healthy weight for any given height.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Factors That Affect Ideal Weight</h2>
              <div className="space-y-4 text-gray-400">
                <div>
                  <h3 className="text-white font-semibold mb-1">Age</h3>
                  <p className="text-sm leading-relaxed">
                    In theory, age shouldn't be a large determinant of an IBW past the ages of 14-15 for girls and 16-17 for boys, 
                    after which most people stop growing.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Gender</h3>
                  <p className="text-sm leading-relaxed">
                    Generally, females weigh less than males even though they naturally have a higher percentage of body fat. 
                    This is because the male body generally has higher muscle mass, and muscle is heavier than fat.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Height</h3>
                  <p className="text-sm leading-relaxed">
                    The taller the person, the more muscle mass and body fat they have, which results in more weight. 
                    A male at a similar height to a female should weigh about 10-20% heavier.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* More Fitness Calculators */}
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness and Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'BMI', path: '/bmi-calculator' },
                { name: 'Calorie', path: '/calorie-calculator' },
                { name: 'Body Fat', path: '/body-fat-calculator' },
                { name: 'BMR', path: '/bmr-calculator' },
                { name: 'Ideal Weight', path: '/ideal-weight-calculator' },
                { name: 'Pregnancy', path: '/pregnancy-calculator' },
                { name: 'Pace', path: '/pace-calculator' },
              ].map((calc) => (
                <Link 
                  key={calc.path}
                  to={calc.path} 
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors"
                >
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default IdealWeightCalculatorPage;
